import React, { createContext, useContext, useState, useEffect } from "react";
import { useSocket } from "./SocketContext";

export enum PlayerRole {
  UNASSIGNED = "unassigned",
  MAFIA = "mafia",
  VILLAGER = "villager",
  DOCTOR = "doctor",
  DETECTIVE = "detective",
}

export enum GamePhase {
  WAITING = "waiting",
  DAY = "day",
  VOTING = "voting", // Add voting phase
  NIGHT = "night",
}

export interface Player {
  id: string;
  name: string;
  isAlive: boolean;
  isReady: boolean;
  role?: PlayerRole;
}

export interface Message {
  sender: string;
  content: string;
  timestamp: number;
}

export interface GameState {
  roomId: string;
  roomCode: string;
  adminId: string;
  players: Player[];
  myRole: PlayerRole;
  myId: string;
  phase: GamePhase;
  dayCount: number;
  isGameStarted: boolean;
  isGameOver: boolean;
  winner: string | null;
  killResult: string | null;
  messages: Message[];
  detectiveResult: { targetId: string; isMafia: boolean } | null;
  // New voting-related fields
  votingResult: string | null;
  eliminatedPlayerId: string | null;
  wasEliminatedMafia: boolean | null;
  voteCount: Record<string, number>;
}

interface GameContextType {
  gameState: GameState;
  createRoom: (name: string) => void;
  joinRoom: (
    code: string,
    name: string
  ) => Promise<{ success: boolean; roomId: string; error?: string }>;
  readyUp: () => void;
  startGame: () => void;
  sendMafiaAction: (targetId: string) => void;
  sendDoctorAction: (targetId: string) => void;
  sendDetectiveAction: (targetId: string) => void;
  sendMessage: (content: string) => void;
  // New voting-related methods
  startVoting: () => void;
  castVote: (targetId: string | null) => void;
  endVoting: () => void;
  startNextPhase: () => void;
}

const initialGameState: GameState = {
  roomId: "",
  roomCode: "",
  adminId: "",
  players: [],
  myRole: PlayerRole.UNASSIGNED,
  myId: "",
  phase: GamePhase.WAITING,
  dayCount: 0,
  isGameStarted: false,
  isGameOver: false,
  winner: null,
  killResult: null,
  messages: [],
  detectiveResult: null,
  // Initialize voting fields
  votingResult: null,
  eliminatedPlayerId: null,
  wasEliminatedMafia: null,
  voteCount: {},
};

const GameContext = createContext<GameContextType>({
  gameState: initialGameState,
  createRoom: () => {},
  joinRoom: async () => ({
    success: false,
    roomId: "",
    error: "Not implemented",
  }),
  readyUp: () => {},
  startGame: () => {},
  sendMafiaAction: () => {},
  sendDoctorAction: () => {},
  sendDetectiveAction: () => {},
  sendMessage: () => {},
  // Default voting methods
  startVoting: () => {},
  castVote: () => {},
  endVoting: () => {},
  startNextPhase: () => {},
});

export const useGame = () => useContext(GameContext);

interface GameProviderProps {
  children: React.ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on("playerJoined", (roomData) => {
      setGameState((prev) => ({
        ...prev,
        players: roomData.players,
      }));
    });

    socket.on("playerLeft", ({ playerId, roomData }) => {
      setGameState((prev) => ({
        ...prev,
        players: roomData.players,
        adminId: roomData.adminId,
      }));
    });

    socket.on("playerReady", ({ playerId, allReady }) => {
      setGameState((prev) => ({
        ...prev,
        players: prev.players.map((p) =>
          p.id === playerId ? { ...p, isReady: true } : p
        ),
      }));
    });

    socket.on("gameStarted", ({ role, roomData }) => {
      setGameState((prev) => ({
        ...prev,
        myRole: role,
        isGameStarted: true,
        phase: roomData.gameState.phase,
        dayCount: roomData.gameState.dayCount,
      }));
    });

    socket.on("phaseChanged", ({ phase, dayCount, killResult, roomData }) => {
      setGameState((prev) => ({
        ...prev,
        phase,
        dayCount,
        killResult,
        players: roomData.players.map((p: any) => ({
          id: p.id,
          name: p.name,
          isAlive: p.isAlive,
          isReady: p.isReady,
          // Preserve role for mafia members
          role: prev.players.find((player) => player.id === p.id)?.role,
        })),
      }));
    });

    socket.on("investigationResult", ({ targetId, isMafia }) => {
      setGameState((prev) => ({
        ...prev,
        detectiveResult: { targetId, isMafia },
      }));
    });

    socket.on("newMessage", (message) => {
      setGameState((prev) => ({
        ...prev,
        messages: [...prev.messages, message],
      }));
    });

    socket.on("gameOver", ({ winner, roomData }) => {
      setGameState((prev) => ({
        ...prev,
        isGameOver: true,
        winner,
        players: roomData.players.map((p: any) => ({
          ...p,
          // Keep role information for the game over screen
          role:
            prev.players.find((player) => player.id === p.id)?.role || p.role,
        })),
      }));
    });

    // New voting-related socket listeners
    socket.on("votingStarted", ({ roomData }) => {
      setGameState((prev) => ({
        ...prev,
        phase: GamePhase.VOTING,
        votingResult: null,
        eliminatedPlayerId: null,
        wasEliminatedMafia: null,
        voteCount: roomData.gameState.voteCount || {},
      }));
    });

    socket.on("voteUpdate", ({ roomData }) => {
      setGameState((prev) => ({
        ...prev,
        voteCount: roomData.gameState.voteCount || {},
      }));
    });

    socket.on("votingResults", ({ roomData }) => {
      setGameState((prev) => ({
        ...prev,
        votingResult: roomData.gameState.votingResult,
        eliminatedPlayerId: roomData.gameState.eliminatedPlayerId,
        wasEliminatedMafia: roomData.gameState.wasEliminatedMafia,
        players: roomData.players.map((p: any) => ({
          id: p.id,
          name: p.name,
          isAlive: p.isAlive,
          isReady: p.isReady,
          role: prev.players.find((player) => player.id === p.id)?.role,
        })),
      }));
    });

    return () => {
      socket.off("playerJoined");
      socket.off("playerLeft");
      socket.off("playerReady");
      socket.off("gameStarted");
      socket.off("phaseChanged");
      socket.off("investigationResult");
      socket.off("newMessage");
      socket.off("gameOver");
      socket.off("votingStarted");
      socket.off("voteUpdate");
      socket.off("votingResults");
    };
  }, [socket]);

  // Check if we're in debug mode when component mounts
  useEffect(() => {
    if (localStorage.getItem("debugGameEnabled") === "true") {
      const debugPhase =
        (localStorage.getItem("debugGamePhase") as GamePhase) || GamePhase.DAY;
      const debugRole =
        (localStorage.getItem("debugGameRole") as PlayerRole) ||
        PlayerRole.VILLAGER;
      const debugPlayerName =
        localStorage.getItem("debugPlayerName") || "DebugPlayer";
      const scenarioIndex = parseInt(
        localStorage.getItem("debugScenarioIndex") || "0",
        10
      );

      // Generate mock data based on the scenario
      const debugPlayers = generateDebugPlayers(debugRole, scenarioIndex);
      const myId = "player-debug";

      // Handle different game over scenarios
      const isGameOver = scenarioIndex === 6 || scenarioIndex === 7;
      const winner =
        scenarioIndex === 6
          ? "mafia"
          : scenarioIndex === 7
          ? "villagers"
          : null;

      // Create mock game state for debugging
      setGameState({
        roomId: "debug-room",
        roomCode: "DEBUG",
        adminId: myId,
        players: debugPlayers,
        myRole: debugRole,
        myId: myId,
        phase: debugPhase,
        dayCount: 2,
        isGameStarted: true,
        isGameOver: isGameOver,
        winner: winner,
        killResult: scenarioIndex === 4 ? "player-victim" : null,
        messages: generateDebugMessages(scenarioIndex),
        detectiveResult:
          debugRole === PlayerRole.DETECTIVE && debugPhase === GamePhase.DAY
            ? { targetId: "player-2", isMafia: true }
            : null,
        votingResult:
          debugPhase === GamePhase.VOTING && scenarioIndex === 5
            ? "voting-results"
            : null,
        eliminatedPlayerId: scenarioIndex === 5 ? "player-2" : null,
        wasEliminatedMafia: scenarioIndex === 5 ? true : null,
        voteCount: { "player-2": 3, "player-3": 1 },
      });

      // Clear debug mode after setting up
      // localStorage.removeItem('debugGameEnabled');
    }
  }, []);

  // Generate mock players for debug mode
  const generateDebugPlayers = (
    myRole: PlayerRole,
    scenarioIndex: number
  ): Player[] => {
    const players: Player[] = [
      {
        id: "player-debug",
        name: localStorage.getItem("debugPlayerName") || "DebugPlayer",
        isAlive: true,
        isReady: true,
        role: myRole,
      },
      {
        id: "player-2",
        name: "Alice",
        isAlive: scenarioIndex !== 5, // dead in voting result scenario
        isReady: true,
        role: PlayerRole.MAFIA,
      },
      {
        id: "player-3",
        name: "Bob",
        isAlive: true,
        isReady: true,
        role: PlayerRole.VILLAGER,
      },
      {
        id: "player-4",
        name: "Charlie",
        isAlive: true,
        isReady: true,
        role:
          myRole === PlayerRole.DETECTIVE
            ? PlayerRole.VILLAGER
            : PlayerRole.DETECTIVE,
      },
      {
        id: "player-5",
        name: "Dave",
        isAlive: true,
        isReady: true,
        role:
          myRole === PlayerRole.DOCTOR
            ? PlayerRole.VILLAGER
            : PlayerRole.DOCTOR,
      },
      {
        id: "player-victim",
        name: "Eve",
        isAlive: false,
        isReady: true,
        role: PlayerRole.VILLAGER,
      },
    ];

    // Make some players dead for game over scenarios
    if (scenarioIndex === 6) {
      // Mafia win - most villagers dead
      players.forEach((p) => {
        if (p.role === PlayerRole.VILLAGER && p.id !== "player-debug") {
          p.isAlive = false;
        }
      });
    } else if (scenarioIndex === 7) {
      // Village win - all mafia dead
      players.forEach((p) => {
        if (p.role === PlayerRole.MAFIA) {
          p.isAlive = false;
        }
      });
    }

    return players;
  };

  // Generate mock chat messages for debug mode
  const generateDebugMessages = (scenarioIndex: number): Message[] => {
    const baseMessages: Message[] = [
      {
        sender: "System",
        content: "Game started!",
        timestamp: Date.now() - 300000,
      },
      {
        sender: "Alice",
        content: "Hello everyone!",
        timestamp: Date.now() - 240000,
      },
      {
        sender: "Bob",
        content: "I think we should be careful of Charlie...",
        timestamp: Date.now() - 180000,
      },
      {
        sender: "Charlie",
        content: "I'm not the mafia, I promise!",
        timestamp: Date.now() - 120000,
      },
    ];

    // Add phase-specific messages
    if (scenarioIndex === 4) {
      // Day after a kill
      baseMessages.push({
        sender: "System",
        content: "Eve was found dead this morning!",
        timestamp: Date.now() - 60000,
      });
    } else if (scenarioIndex === 5) {
      // Voting phase
      baseMessages.push({
        sender: "Dave",
        content: "I'm voting for Alice, she's been too quiet",
        timestamp: Date.now() - 60000,
      });
    }

    return baseMessages;
  };

  const createRoom = (name: string) => {
    if (!socket) return;

    socket.emit("createRoom", { name }, (response: any) => {
      if (response.success) {
        setGameState((prev) => ({
          ...prev,
          roomId: response.roomId,
          roomCode: response.roomCode,
          adminId: socket.id || "",
          myId: socket.id || "",
          players: response.roomData.players,
        }));
      }
    });
  };

  const joinRoom = async (
    code: string,
    name: string
  ): Promise<{ success: boolean; roomId: string; error?: string }> => {
    return new Promise((resolve) => {
      if (!socket) {
        resolve({ success: false, roomId: "", error: "Socket not connected" });
        return;
      }

      socket.emit("joinRoom", { code, name }, (response: any) => {
        if (response.success) {
          setGameState((prev) => ({
            ...prev,
            roomId: response.roomData.id,
            roomCode: response.roomData.code,
            adminId: response.roomData.adminId,
            players: response.roomData.players,
            myId: response.playerId,
            messages: response.roomData.messages || [],
          }));
          resolve({ success: true, roomId: response.roomData.id });
        } else {
          console.error(response.error);
          resolve({ success: false, roomId: "", error: response.error });
        }
      });
    });
  };

  const readyUp = () => {
    if (!socket || !gameState.roomId) return;

    socket.emit("readyUp", { roomId: gameState.roomId });
  };

  const startGame = () => {
    if (!socket || !gameState.roomId) return;

    socket.emit("startGame", { roomId: gameState.roomId }, (response: any) => {
      if (!response.success) {
        console.error(response.error);
      }
    });
  };

  const sendMafiaAction = (targetId: string) => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.NIGHT)
      return;

    socket.emit("mafiaAction", { roomId: gameState.roomId, targetId });
  };

  const sendDoctorAction = (targetId: string) => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.NIGHT)
      return;

    socket.emit("doctorAction", { roomId: gameState.roomId, targetId });
  };

  const sendDetectiveAction = (targetId: string) => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.NIGHT)
      return;

    socket.emit("detectiveAction", { roomId: gameState.roomId, targetId });
  };

  const sendMessage = (content: string) => {
    if (!socket || !gameState.roomId || !content.trim()) return;

    socket.emit("sendMessage", { roomId: gameState.roomId, content });
  };

  // New voting-related methods
  const startVoting = () => {
    if (!socket || !gameState.roomId) return;

    socket.emit(
      "startVoting",
      { roomId: gameState.roomId },
      (response: any) => {
        if (!response?.success) {
          console.error(response?.error || "Could not start voting");
        }
      }
    );
  };

  const castVote = (targetId: string | null) => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.VOTING)
      return;

    socket.emit(
      "castVote",
      { roomId: gameState.roomId, targetId },
      (response: any) => {
        if (!response?.success) {
          console.error(response?.error || "Could not cast vote");
        }
      }
    );
  };

  const endVoting = () => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.VOTING)
      return;

    socket.emit("endVoting", { roomId: gameState.roomId }, (response: any) => {
      if (!response?.success) {
        console.error(response?.error || "Could not end voting");
      }
    });
  };

  const startNextPhase = () => {
    if (!socket || !gameState.roomId || gameState.phase !== GamePhase.VOTING)
      return;

    socket.emit(
      "startNextPhase",
      { roomId: gameState.roomId },
      (response: any) => {
        if (!response?.success) {
          console.error(response?.error || "Could not start next phase");
        }
      }
    );
  };

  return (
    <GameContext.Provider
      value={{
        gameState,
        createRoom,
        joinRoom,
        readyUp,
        startGame,
        sendMafiaAction,
        sendDoctorAction,
        sendDetectiveAction,
        sendMessage,
        // Add voting methods
        startVoting,
        castVote,
        endVoting,
        startNextPhase,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
